import { NiceScale } from "modules/common/components/charts/NiceScale";

/**
 * Generates nice-looking tick values for chart axes
 *
 * @param min The minimum data value
 * @param max The maximum data value
 * @param maxTicks The maximum number of ticks to generate
 * @param forceZeroMin Whether to force the minimum to be zero (default: true)
 * @returns An array of nicely spaced tick values
 */
export function generateNiceYAxisTicks(
  min: number,
  max: number,
  maxTicks: number = 5,
  forceZeroMin: boolean = true
): number[] {
  // Handle case where min and max are too close or identical
  if (max - min < 0.00001) {
    max = min + 1;
  }

  // If forcing zero minimum is required
  if (forceZeroMin) {
    min = 0;
  }

  // Create a nice scale instance
  const niceScale = new NiceScale(min, max, maxTicks);

  // Return the ticks
  return niceScale.getTicks();
}

/**
 * Formats a tick value with appropriate precision
 *
 * @param value The tick value to format
 * @param range The data range
 * @returns A formatted string representation of the tick value
 */
export function formatNiceTickValue(value: number, range: number): string {
  // For zero, always return "0" without decimal places
  if (Math.abs(value) < 0.00001) {
    return "0";
  }

  // Check if value is very close to a whole number
  const isNearWhole = Math.abs(value - Math.round(value)) < 0.001;

  // For values very close to whole numbers, show as integers
  if (isNearWhole) {
    return Math.round(value).toString();
  }

  // Determine decimal precision based on the magnitude of the range
  const rangeExponent = Math.floor(Math.log10(range));

  if (rangeExponent < -2) {
    // For very small ranges, show more decimal places
    return value.toFixed(Math.abs(rangeExponent) + 1);
  } else if (rangeExponent < 0) {
    // For small ranges
    return value.toFixed(2);
  } else if (rangeExponent < 1) {
    // For medium ranges
    return value.toFixed(1);
  } else {
    // For large ranges
    return Math.round(value).toString();
  }
}
